import { useCallback, useState } from 'react';

type ErrorType = 'unicityError' | 'anyError';

type ErrorMessageByStatus = Partial<Record<number, string>>;

type SubmitParamsType<T> = {
  callbacks: (() => Promise<T> | T)[];
  errorMessages: Record<ErrorType, string>;
  successMessage: string;
  statusErrorMessages?: ErrorMessageByStatus;
};

type BackendErrorLike = {
  status?: number;
  response?: {
    status?: number;
    data?: unknown;
  };
};

export type UseSubmitNotifier = (
  message: string,
  severity: 'success' | 'error'
) => void;

export type UseSubmitOptions = {
  notify?: UseSubmitNotifier;
};

function extractStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const typedError = error as BackendErrorLike;
  const status = typedError.response?.status ?? typedError.status;
  return typeof status === 'number' && Number.isFinite(status)
    ? status
    : undefined;
}

function extractBackendMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const typedError = error as BackendErrorLike;
  const data = typedError.response?.data;

  if (typeof data === 'string' && data.trim().length > 0) {
    return data.trim();
  }

  if (!data || typeof data !== 'object') {
    return undefined;
  }

  const recordData = data as Record<string, unknown>;

  const simpleMessageCandidates = ['message', 'detail', 'error'];
  for (const key of simpleMessageCandidates) {
    const value = recordData[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  if (Array.isArray(recordData.detail)) {
    const detailLines = recordData.detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object') {
          const detailRecord = item as Record<string, unknown>;
          if (
            typeof detailRecord.msg === 'string' &&
            detailRecord.msg.trim().length > 0
          ) {
            return detailRecord.msg.trim();
          }
        }

        return '';
      })
      .filter((line) => line.length > 0);

    if (detailLines.length > 0) {
      return detailLines.join(' | ');
    }
  }

  return undefined;
}

function resolveErrorMessage<T>(
  params: SubmitParamsType<T>,
  error: unknown
): string {
  const statusCode = extractStatusCode(error);
  const backendMessage = extractBackendMessage(error);
  const knownStatusMessage =
    typeof statusCode === 'number'
      ? params.statusErrorMessages?.[statusCode]
      : undefined;

  if (knownStatusMessage && backendMessage) {
    const normalizedKnownStatus = knownStatusMessage.trim().toLowerCase();
    const normalizedBackend = backendMessage.trim().toLowerCase();

    if (
      normalizedKnownStatus.length > 0 &&
      normalizedKnownStatus !== normalizedBackend
    ) {
      return `${knownStatusMessage} (${backendMessage})`;
    }

    return knownStatusMessage;
  }

  if (knownStatusMessage) {
    return knownStatusMessage;
  }

  if (backendMessage) {
    return backendMessage;
  }

  if (statusCode === 409) {
    return params.errorMessages.unicityError;
  }

  return params.errorMessages.anyError;
}

export const useSubmit = <T>(options?: UseSubmitOptions) => {
  const [state, setState] = useState<'Idle' | 'Loading' | 'Success' | 'Error'>(
    'Idle'
  );
  const [error, setError] = useState<unknown>(null);

  const reset = () => {
    setState('Idle');
  };

  const submit = useCallback(
    async (params: SubmitParamsType<T>) => {
      setState('Loading');

      try {
        for (const func of params.callbacks) {
          await func();
        }

        setState('Success');
        options?.notify?.(params.successMessage, 'success');
      } catch (err: unknown) {
        setState('Error');
        setError(err);

        const errorMessage = resolveErrorMessage(params, err);
        options?.notify?.(errorMessage, 'error');
      }
    },
    [options]
  );

  return { submit, state, error, reset };
};
